import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Typography,
  Divider,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import tripayService from '../services/tripayService';
import { TripayConfig, TripayConfigInput } from '../types/tripay';

const TripaySettings: React.FC = () => {
  const [config, setConfig] = useState<TripayConfig | null>(null);
  const [formData, setFormData] = useState<TripayConfigInput>({
    api_key: '',
    private_key: '',
    merchant_code: '',
    is_production: false,
    callback_url: '',
    return_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [gatewayEnabled, setGatewayEnabled] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await tripayService.config.getConfig();
      
      if (response.success && response.data) {
        setConfig(response.data);
        setGatewayEnabled(response.data.is_enabled !== false);
        setFormData({
          api_key: '', // Don't show existing API key for security
          private_key: '', // Don't show existing private key for security
          merchant_code: response.data.merchant_code,
          is_production: response.data.is_production,
          callback_url: response.data.callback_url || '',
          return_url: response.data.return_url || ''
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TripayConfigInput) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'is_production' ? event.target.checked : event.target.value;
    setFormData((prev: TripayConfigInput) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!config) {
      setError('Simpan konfigurasi Tripay terlebih dahulu sebelum mengaktifkan gateway');
      return;
    }
    try {
      setToggling(true);
      setError('');
      const response = await tripayService.config.toggleEnabled(enabled);
      if (response.success) {
        setGatewayEnabled(enabled);
        setConfig((prev) => (prev ? { ...prev, is_enabled: enabled } : prev));
        setSuccess(response.message || (enabled ? 'Tripay diaktifkan' : 'Tripay dinonaktifkan'));
      } else {
        setError(response.message || 'Gagal mengubah status gateway');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal mengubah status gateway');
    } finally {
      setToggling(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!formData.api_key || !formData.private_key || !formData.merchant_code) {
      setError('API Key, Private Key, and Merchant Code are required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await tripayService.config.updateConfig(formData);
      
      if (response.success) {
        setSuccess('Configuration updated successfully');
        setConfig(response.data);
        // Clear sensitive fields after saving
        setFormData((prev: TripayConfigInput) => ({
          ...prev,
          api_key: '',
          private_key: ''
        }));
      } else {
        setError(response.message || 'Failed to update configuration');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Tripay Payment Gateway Settings"
        subheader="Configure Tripay payment gateway integration"
        action={
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadConfig}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: gatewayEnabled ? 'success.light' : 'divider',
            bgcolor: gatewayEnabled ? 'success.50' : 'grey.50',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Status Gateway Tripay
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {config
                  ? gatewayEnabled
                    ? 'Aktif — pembayaran Tripay dapat dibuat'
                    : 'Nonaktif — pembayaran Tripay tidak tersedia'
                  : 'Belum dikonfigurasi — simpan konfigurasi dulu'}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                label={gatewayEnabled ? 'AKTIF' : 'NONAKTIF'}
                color={gatewayEnabled ? 'success' : 'default'}
                size="small"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={gatewayEnabled}
                    onChange={(e) => handleToggleEnabled(e.target.checked)}
                    disabled={!config || toggling}
                    color="success"
                  />
                }
                label={gatewayEnabled ? 'Aktif' : 'Nonaktif'}
              />
            </Stack>
          </Stack>
        </Box>

        {config && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Current Configuration
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Merchant Code: <strong>{config.merchant_code}</strong>
                </Typography>
                <Chip
                  label={config.is_production ? 'Production' : 'Sandbox'}
                  color={config.is_production ? 'error' : 'primary'}
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Base URL: <strong>{config.base_url}</strong>
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Status:
                </Typography>
                <Chip
                  label={config.is_active ? 'Active' : 'Inactive'}
                  color={config.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="API Key"
              type="password"
              value={formData.api_key}
              onChange={handleInputChange('api_key')}
              required
              helperText="Your Tripay API Key (will be hidden after saving)"
            />

            <TextField
              fullWidth
              label="Private Key"
              type="password"
              multiline
              rows={3}
              value={formData.private_key}
              onChange={handleInputChange('private_key')}
              required
              helperText="Your Tripay Private Key for signature generation (will be hidden after saving)"
            />

            <TextField
              fullWidth
              label="Merchant Code"
              value={formData.merchant_code}
              onChange={handleInputChange('merchant_code')}
              required
              helperText="Your Tripay Merchant Code"
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_production}
                    onChange={handleInputChange('is_production')}
                    color="error"
                  />
                }
                label="Production Environment"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Use production environment (unchecked = sandbox)
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Callback URL"
              value={formData.callback_url}
              onChange={handleInputChange('callback_url')}
              placeholder="https://yourdomain.com/api/tripay/callback"
              helperText="URL for receiving payment notifications from Tripay"
            />

            <TextField
              fullWidth
              label="Return URL"
              value={formData.return_url}
              onChange={handleInputChange('return_url')}
              placeholder="https://yourdomain.com/payment/success"
              helperText="URL where customers will be redirected after payment"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
                sx={{ minWidth: 120 }}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Configuration'}
              </Button>
              
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setFormData({
                    api_key: '',
                    private_key: '',
                    merchant_code: '',
                    is_production: false,
                    callback_url: '',
                    return_url: ''
                  });
                  setError('');
                  setSuccess('');
                }}
              >
                Reset Form
              </Button>
            </Box>
          </Stack>
        </form>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.main', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText">
            <strong>Important:</strong> Make sure to configure the callback URL in your Tripay merchant dashboard
            to receive payment notifications. The callback URL should be accessible from the internet.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TripaySettings;
